import type { Processor } from '#src/graphql/generated';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import { useState } from 'react';

import { ExecuteProcessorModal } from '#src/components/execute-processor-modal';
import { UpsertProcessorDrawer } from '#src/components/upsert-processor-drawer';
import { GET_PROCESSORS } from '#src/graphql/queries/processors';

export function ProcessorsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProcessorId, setEditingProcessorId] = useState<
    string | undefined
  >();
  const [isUpsertDrawerOpen, setIsUpsertDrawerOpen] = useState(false);
  const [executeProcessorId, setExecuteProcessorId] = useState<
    string | undefined
  >();
  const [executeProcessorName, setExecuteProcessorName] = useState<
    string | undefined
  >();

  const { data, loading, error, refetch } = useQuery(GET_PROCESSORS);

  const filteredProcessors =
    data?.processors?.processors?.filter((p) =>
      p.id.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const handleEdit = (processor: Processor) => {
    setEditingProcessorId(processor.id);
    setIsUpsertDrawerOpen(true);
  };

  const handleCreate = () => {
    setEditingProcessorId(undefined);
    setIsUpsertDrawerOpen(true);
  };

  const handleCloseUpsertDrawer = () => {
    setEditingProcessorId(undefined);
    setIsUpsertDrawerOpen(false);
    refetch();
  };

  const handleExecute = (processor: Processor) => {
    setExecuteProcessorId(processor.id);
    setExecuteProcessorName(processor.id);
  };

  const handleCloseExecuteModal = () => {
    setExecuteProcessorId(undefined);
    setExecuteProcessorName(undefined);
  };

  if (loading) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          Processors
        </Heading>
        <Text>Loading processors...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          Processors
        </Heading>
        <Text color="red.500">Error loading processors: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={4} align="stretch">
          <HStack>
            <Input
              placeholder="Search processors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              flex={1}
            />
            <Button onClick={() => refetch()} size="sm">
              Refresh
            </Button>
            <Button onClick={handleCreate} size="sm" colorScheme="blue">
              Create Processor
            </Button>
          </HStack>

          {filteredProcessors.length === 0 ? (
            <Text color="fg.muted" textAlign="center" py={8}>
              {searchTerm
                ? 'No processors match your search.'
                : 'No processors yet.'}
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {filteredProcessors.map((processor) => (
                <Card.Root key={processor.id} variant="outline">
                  <CardHeader pb={2}>
                    <HStack justify="space-between" align="start">
                      <Text fontWeight="semibold" fontSize="md">
                        {processor.id}
                      </Text>
                      <Badge colorScheme="green" variant="subtle" fontSize="xs">
                        {new Date(processor.updatedAt).toLocaleDateString()}
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Text
                      fontSize="sm"
                      color="fg.muted"
                      mb={4}
                      lineClamp={3}
                      fontFamily="mono"
                      whiteSpace="pre"
                      overflow="hidden"
                    >
                      {processor.code}
                    </Text>
                    <HStack gap={2}>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        flex={1}
                        onClick={() => handleEdit(processor)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="green"
                        flex={1}
                        onClick={() => handleExecute(processor)}
                      >
                        Execute
                      </Button>
                    </HStack>
                  </CardBody>
                </Card.Root>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Box>

      <UpsertProcessorDrawer
        open={isUpsertDrawerOpen}
        processorId={editingProcessorId}
        onClose={handleCloseUpsertDrawer}
      />

      {executeProcessorId && (
        <ExecuteProcessorModal
          open={!!executeProcessorId}
          processorId={executeProcessorId}
          processorName={executeProcessorName}
          onClose={handleCloseExecuteModal}
        />
      )}
    </Box>
  );
}
